import {
  Button,
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
  message,
  useUpdate,
  MultiSelect,
} from '@maxtropy/components';
import { Space } from 'antd';
import React, { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import {
  apiV2PipelinePressureMonitoringAddPressureMonitoringTaskPost,
  apiV2PipelinePressureMonitoringDeletePipelinePressureMonitoringPost,
  apiV2PipelinePressureMonitoringGetAllMonitorConfigPost,
  apiV2PipelinePressureMonitoringPipelinePressureMonitoringPagePost,
  apiV2PipelinePressureMonitoringReExecutePipelinePressureMonitoringPost,
} from '@maxtropy/device-customer-apis-v2';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import { isNil } from 'lodash-es';

// 模拟数据类型定义
export interface PipelinePressureTask {
  id: number;
  taskName: string;
  zoneName: string[];
  monitorTimeRange: [string, string];
  generateStatus: number;
  operationTime: string;
  operator: string;
}

// 筛选条件类型
export interface FilterParams {
  taskName?: string;
  monitorTimeRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

// 监测时间间隔选项
const TIME_INTERVAL_OPTIONS = [
  { label: '15', value: 15 },
  { label: '30', value: 30 },
  { label: '45', value: 45 },
  { label: '60', value: 60 },
];

// 表格列配置
const columns = [
  {
    title: '压力监测任务名称',
    dataIndex: 'taskName',
    width: 200,
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '监测区域',
    dataIndex: 'zoneName',
    width: 200,
    ellipsis: true,
    render: (v: string[]) => <EllipsisSpan value={v?.join(',')} />,
  },
  {
    title: '最大降压幅度',
    dataIndex: 'pressureDropMagnitude',
    width: 200,
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={!isNil(v) ? v + '%' : '--'} />,
  },
  {
    title: '监测时间',
    dataIndex: 'monitorTimeRange',
    width: 250,
    ellipsis: true,
    render: (v: [string, string]) => <EllipsisSpan value={`${v?.[0] || '-'} ~ ${v?.[1] || '-'}`} />,
  },
  {
    title: '状态',
    dataIndex: 'generateStatus',
    width: 100,
    ellipsis: true,
    render: (status: number) => (
      <Tag border="solid" color={status === 1 ? 'warning' : 'success'}>
        {status === 1 ? '待生成' : '已生成'}
      </Tag>
    ),
  },
  {
    title: '操作时间',
    dataIndex: 'operationTime',
    ellipsis: true,
    width: 200,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '操作人',
    dataIndex: 'operator',
    width: 200,
    ellipsis: true,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const PipelinePressureMonitoring: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [updateState, updateFn] = useUpdate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchParams, setSearchParams] = useState<FilterParams>({});
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const hasConfig = useHasPermission(PermissionsType.B_PIPELINEPRESSUREMONITORINGEDIT);
  const hasEXECUTE = useHasPermission(PermissionsType.B_MANUALLYARE_EXECUTE);
  const { data: option, loading: optionLoading } = useRequest(
    () =>
      apiV2PipelinePressureMonitoringGetAllMonitorConfigPost({}).then(res => {
        const formatData = (list: any[]): any[] => {
          return list.map(item => ({
            label: item.areaInfo.map((area: any) => area.zoneName).join(','),
            value: item.id,
            key: item.id,
          }));
        };
        return formatData(res.list || []);
      }),
    {
      ready: isModalVisible,
      refreshDeps: [isModalVisible],
    }
  );

  const { data: tableData, loading } = useRequest(
    async () => {
      const res = await apiV2PipelinePressureMonitoringPipelinePressureMonitoringPagePost({
        taskName: searchParams.taskName,
        startTime: searchParams.monitorTimeRange?.[0].startOf('days').valueOf(),
        endTime: searchParams.monitorTimeRange?.[1].endOf('days').valueOf(),
        page: pageOffset,
        size: pageSize,
      });
      const list = res.list || [];
      setTotalCount(res.total!);
      return list.map((item: any) => ({
        id: item.id,
        taskName: item.taskName,
        zoneName: item.areaInfo?.map((area: any) => area.zoneName) || [],
        monitorTimeRange: [
          dayjs(item.startTime).format('YYYY-MM-DD HH:mm:ss'),
          dayjs(item.endTime).format('YYYY-MM-DD HH:mm:ss'),
        ] as [string, string],
        generateStatus: item.generateStatus,
        operationTime: dayjs(item.operationTime).format('YYYY-MM-DD HH:mm:ss') || '-',
        operator: item.operator || '-',
        pressureDropMagnitude: item.pressureDropMagnitude,
      }));
    },
    {
      refreshDeps: [pageOffset, pageSize, searchParams, updateState],
    }
  );

  // 新增
  const handleAdd = () => {
    setIsModalVisible(true);
  };

  // 查看详情 - 跳转到四级页面
  const handleView = (record: PipelinePressureTask) => {
    navigate(`/pipeline/pressure/monitoring/detail/${record.id}`, {
      state: { taskName: record.taskName },
    });
  };

  // 手动重新执行
  const handleReExecute = (item: PipelinePressureTask) => {
    Modal.confirm({
      title: '确认重新执行',
      content: `确定要重新执行任务"${item.taskName}"吗？`,
      onOk: () => {
        apiV2PipelinePressureMonitoringReExecutePipelinePressureMonitoringPost({ id: item.id }).then(() => {
          message.success('任务已重新执行');
          updateFn();
        });
      },
    });
  };

  // 删除
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除任务吗？删除后不可恢复。`,
      onOk: () => {
        apiV2PipelinePressureMonitoringDeletePipelinePressureMonitoringPost({ id }).then(() => {
          message.success('删除成功');
          updateFn();
        });
      },
    });
  };

  // 弹窗确认
  const handleModalOk = () => {
    modalForm.validateFields().then(values => {
      const startTime = values.startTime.valueOf();
      const endTime = values.endTime.valueOf();
      const payload = {
        taskName: values.taskName,
        areaIds: values.areaId,
        startTime,
        endTime,
        timeInterval: values.timeInterval,
      };
      apiV2PipelinePressureMonitoringAddPressureMonitoringTaskPost(payload).then(() => {
        message.success('新增成功');
        handleCancel();
        updateFn();
      });
    });
  };

  // 跳转配置页面
  const handleConfig = () => {
    navigate('/pipeline/pressure/monitoring/config');
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      render: (_: unknown, record: PipelinePressureTask) => (
        <Space size="small">
          {record.generateStatus === 2 && (
            <Button type="link" onClick={() => handleView(record)}>
              查看
            </Button>
          )}

          {hasEXECUTE && (
            <Button type="link" onClick={() => handleReExecute(record)}>
              手动重新执行
            </Button>
          )}
          <Button type="link" danger onClick={() => handleDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 筛选条件
  const onFinish = (values: FilterParams) => {
    setPageOffset(1);
    setSearchParams(values);
  };

  const onReset = () => {
    setPageOffset(1);
    setSearchParams({});
    form.resetFields();
  };
  const handleCancel = () => {
    modalForm.resetFields();
    setIsModalVisible(false);
  };
  const filters = (
    <CustomFilter<FilterParams> form={form} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="taskName" label="压力监测任务名称">
        <Input placeholder="请输入任务名称" allowClear />
      </Form.Item>
      <Form.Item name="monitorTimeRange" label="监测时间">
        <DatePicker.RangePicker format="YYYY-MM-DD" />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className={styles.wrapper}>
      <div className={styles.headerActions}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增压力监测任务
        </Button>
        {hasConfig && (
          <Space>
            <Button type="primary" onClick={handleConfig}>
              配置
            </Button>
          </Space>
        )}
      </div>
      <Table
        rowKey="id"
        sticky
        loading={loading}
        columns={buildColumns}
        dataSource={tableData ?? []}
        scroll={{ x: 1200 }}
      />
      <Paging pagingInfo={pagingInfo} />

      {/* 新增/编辑弹窗 */}
      <Modal
        title="新增压力监测任务"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleCancel}
        width={600}
        destroyOnClose
        contentClassName="modal-form-content"
        className={styles['pipeline-pressure-modal']}
      >
        <Form form={modalForm}>
          <Form.Item
            name="taskName"
            label="压力监测任务名称"
            labelCol={{ span: 10 }}
            rules={[
              { required: true, message: '请输入名称' },
              { min: 1, max: 50, message: '任务名称长度为 1-50 个字符' },
            ]}
          >
            <Input placeholder="请输入任务名称" maxLength={50} />
          </Form.Item>

          <Form.Item name="areaId" label="监测区域" rules={[{ required: true, message: '请选择监测区域' }]}>
            <MultiSelect
              placeholder="请选择"
              loading={optionLoading}
              options={option}
              showSearch
              allowClear
              filterOption={(search, node) => {
                const searchText = (node?.label ?? '') as string;
                return searchText.toLowerCase().includes(search.toLowerCase());
              }}
            />
          </Form.Item>

          <Form.Item label="监测时间范围" required>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item name="startTime" noStyle rules={[{ required: true, message: '请选择开始时间' }]}>
                <DatePicker
                  showTime={{
                    format: 'HH:mm',
                    hideDisabledOptions: true,
                    disabledHours: () => [],
                    disabledMinutes: () => {
                      // 只允许选择 00、15、30、45，其他分钟禁用
                      const allowedMinutes = [0, 15, 30, 45];
                      const allMinutes = Array.from({ length: 60 }, (_, i) => i);
                      return allMinutes.filter(m => !allowedMinutes.includes(m));
                    },
                  }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="请选择"
                  style={{ width: 200 }}
                  onChange={() => {
                    modalForm.validateFields(['endTime']);
                  }}
                />
              </Form.Item>
              <span>~</span>
              <Form.Item
                name="endTime"
                noStyle
                rules={[
                  { required: true, message: '请选择结束时间' },
                  {
                    validator: (_, value) => {
                      const startTime = modalForm.getFieldValue('startTime');
                      if (!value) return Promise.resolve();
                      if (startTime && value.isBefore(startTime)) {
                        return Promise.reject(new Error('结束时间必须晚于开始时间'));
                      }
                      if (startTime) {
                        const diffHours = value.diff(startTime, 'hour', true);
                        if (diffHours > 72) {
                          return Promise.reject(new Error('时间跨度不得超过 72 小时'));
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  showTime={{
                    format: 'HH:mm',
                    hideDisabledOptions: true,
                    disabledHours: () => [],
                    disabledMinutes: () => {
                      // 只允许选择 00、15、30、45，其他分钟禁用
                      const allowedMinutes = [0, 15, 30, 45];
                      const allMinutes = Array.from({ length: 60 }, (_, i) => i);
                      return allMinutes.filter(m => !allowedMinutes.includes(m));
                    },
                  }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="请选择"
                  style={{ width: 200 }}
                  onChange={() => {
                    modalForm.validateFields(['startTime']);
                  }}
                />
              </Form.Item>
            </div>
          </Form.Item>
          <div style={{ display: 'flex' }}>
            <Form.Item
              name="timeInterval"
              label="监测时间间隔"
              initialValue={30}
              rules={[{ required: true, message: '请选择监测时间间隔' }]}
            >
              <Select placeholder="请选择" options={TIME_INTERVAL_OPTIONS} style={{ width: 150 }} />
            </Form.Item>
            <span style={{ color: 'var(--mx-text-secondary-color)', padding: 6 }}>分钟</span>
          </div>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default PipelinePressureMonitoring;
